import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, BarChart3, ExternalLink, FileText, ListChecks, MapPinned, MapPin, MessageSquare } from 'lucide-react'
import moment from 'moment'
import ReactMarkdown from 'react-markdown'
import Layout from '../../components/Layout'
import Seo from '../../components/Seo'
import { handleCommentSubmit, handleGeneratePrompt, handleSubmitAnalysis } from '../../helpers/handleDetailActions'
import Gallery from '../../components/Gallery'
import ListingMap from '../../components/ListingMap'
import { getGalleryImageUrl } from '../../helpers/galleryHelpers'
import ComparableTable from '../Valuations/ComparableTable'
import ComparablePropertyModal from '../Valuations/ComparablePropertyModal'
import FeasibilityPreviewModal from './FeasibilityPreviewModal'
import { VALUATION_METHODS, getValuationMethodLabel } from '../../helpers/valuationMethods'
import { FLOOR_CONDITION } from '../../helpers/valuationConditions'
import { feasibilityService } from '../../services/feasibilityService'
import { getFeasibilityForm, prepareFeasibilityCalculation } from '../../helpers/feasibility'
import { valuationService } from '../../services/valuationService'
import { propertyValuationCacheService } from '../../services/propertyValuationCacheService'
import { getDealTone, getFloorDealComparison, getRenovatedDealComparison } from '../../helpers/propertyDealScore'
import FeasibilityCalculationResult from '../../components/FeasibilityCalculationResult'
import PublicValuationLayout from '../Valuations/PublicValuationLayout'
import { publicPropertyDemoService } from '../../services/publicPropertyDemoService'
import ValuationEvidenceSummary from '../../components/ValuationEvidenceSummary'
import PropertyInfrastructure from '../../components/PropertyInfrastructure'
import PropertyScrapeDebug from '../../components/PropertyScrapeDebug'

const API_BASE_URL = 'https://api.realitni-jestrab.cz'

const MANUAL_EDIT_FIELD_LABELS = {
  m2: 'Výměra',
  condition: 'Stav',
  floor: 'Podlaží',
  hasElevator: 'Výtah',
  underRoof: 'Pod střechou',
  ignoreForComparisons: 'Ignorovat ve srovnáních',
}

const formatManualEditValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'neuvedeno'
  if (field === 'm2') return `${value} m²`
  if (field === 'hasElevator' || field === 'underRoof') return value ? 'Ano' : 'Ne'
  return String(value)
}

const getLinkHostname = (value) => {
  try {
    return new URL(value).hostname
  } catch (error) {
    return 'Původní nabídka'
  }
}

const ManualEditHistory = ({ property }) => {
  const history = property.manualEdits?.history || []
  if (!history.length) return null

  const latest = history[history.length - 1]
  return (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
      <div className="font-semibold">Ručně upravené údaje</div>
      <div className="mt-1 text-xs text-amber-800">
        Naposledy upravil {latest.editedBy?.displayName || 'neznámý uživatel'} dne{' '}
        {moment(latest.editedAt).isValid() ? moment(latest.editedAt).format('DD.MM.YYYY HH:mm') : '—'}.
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-medium">Zobrazit historii změn ({history.length})</summary>
        <div className="mt-3 space-y-3">
          {[...history].reverse().map((edit, index) => (
            <div key={`${edit.editedAt || 'edit'}:${index}`} className="rounded border border-amber-200 bg-white/70 p-3">
              <div className="text-xs font-medium">
                {edit.editedBy?.displayName || 'Neznámý uživatel'} ·{' '}
                {moment(edit.editedAt).isValid() ? moment(edit.editedAt).format('DD.MM.YYYY HH:mm') : '—'}
              </div>
              <div className="mt-2 space-y-1 text-xs">
                {Object.entries(edit.changes || {}).map(([field, change]) => (
                  <div key={field}>
                    {MANUAL_EDIT_FIELD_LABELS[field] || field}: {formatManualEditValue(field, change.from)} → {formatManualEditValue(field, change.to)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  )
}

const Description = ({ property }) => {
  const paragraphs = String(property.desc || '').split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean)

  return (
    <div className="max-w-4xl space-y-5 text-[15px] leading-7 text-slate-700 sm:text-base sm:leading-8">
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="whitespace-pre-line">
          {paragraph}
        </p>
      ))}
    </div>
  )
}

const DetailTabs = ({ tabs, activeTab, onChange, children }) => (
  <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-200 bg-slate-50/80 p-2 sm:p-3">
      <div role="tablist" aria-label="Detail nabídky" className="flex gap-2 overflow-x-auto pb-0.5">
        {tabs.map(({ id, label, Icon, mobileOnly }) => {
          const active = activeTab === id
          return (
            <button
              key={id}
              id={`detail-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`detail-panel-${id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(id)}
              className={`inline-flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium transition sm:px-4 ${mobileOnly ? 'md:hidden' : ''} ${
                active
                  ? 'bg-slate-950 text-white shadow-sm'
                  : 'border border-transparent text-slate-600 hover:border-slate-200 hover:bg-white hover:text-slate-950'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          )
        })}
      </div>
    </div>
    <div
      id={`detail-panel-${activeTab}`}
      role="tabpanel"
      aria-labelledby={`detail-tab-${activeTab}`}
      className="min-h-56 p-4 sm:p-6"
    >
      {children}
    </div>
  </section>
)

const PropertyDetails = ({ property }) => {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base sm:text-lg font-semibold">Výměra</h3>
        <p className="text-sm sm:text-base leading-6 sm:leading-7">{property.m2} m²</p>
      </div>
      <div>
        <h3 className="mb-2 text-base font-semibold sm:text-lg">Infrastruktura</h3>
        <PropertyInfrastructure value={property.infrastruktura} />
      </div>
      <div>
        <h3 className="text-base sm:text-lg font-semibold">Historie</h3>
        <p className="text-sm sm:text-base leading-6 sm:leading-7">{property.historie || 'Neuvedeno'}</p>
      </div>
    </div>
  )
}

const Analysis = ({ property, showSubmitAnalysis, analysis, prompt, setPrompt, setAnalysis, setShowSubmitAnalysis }) => {
  return (
    <>
      <div className="bg-gray-100 p-4 mt-2 rounded shadow">
        <h3 className="text-base sm:text-lg font-semibold mb-4">Generate AI Prompt</h3>
        <textarea
          className="w-full p-2 border rounded mb-2 h-20 text-sm sm:text-base"
          placeholder="Enter prompt details"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        ></textarea>
        <button className="px-4 py-2 bg-green-500 text-white rounded" onClick={() => handleGeneratePrompt(prompt, property.propertyId, setShowSubmitAnalysis)}>
          Generate AI Prompt
        </button>
        {showSubmitAnalysis && (
          <button className="px-4 py-2 bg-green-500 text-white rounded" onClick={() => handleSubmitAnalysis(analysis, property.propertyId, setShowSubmitAnalysis)}>
            Generate AI Analysis
          </button>
        )}
      </div>

      <ReactMarkdown className="markdown-body mt-4">{property.analyza?.text}</ReactMarkdown>
    </>
  )
}

const Comments = ({ property, comment, setComment, handleCommentSubmit, setProperty }) => {
  return (
    <div className="bg-gray-100 p-4 rounded shadow">
      <h3 className="text-base sm:text-lg font-semibold mb-4">Komentáře</h3>
      <div className="mb-4">
        <textarea
          className="w-full p-2 border rounded mb-2 text-sm sm:text-base"
          placeholder="Add a comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        ></textarea>
        <button className="px-4 py-2 bg-blue-500 text-white rounded" onClick={() => handleCommentSubmit(comment, property.propertyId, setProperty, setComment)}>
          Uložit
        </button>
      </div>
      <div className="space-y-2">
        {property.comments && property.comments.length > 0 ? (
          property.comments.map((c, index) => (
            <div key={index} className="p-2 border-b border-gray-300">
              {moment(c.date).format('HH:mm DD.MM.YYYY')}
              <br />
              {c.text}
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-600">No comments yet.</p>
        )}
      </div>
    </div>
  )
}

const formatPrice = (value) => (Number.isFinite(value) ? `${Math.round(value).toLocaleString('cs-CZ')} Kč` : '—')

const ScenarioSummary = ({ title, description, value, method, comparison, publicView = false }) => {
  if (!value) return <div className="h-full animate-pulse rounded-lg bg-gray-100 p-4 text-sm text-gray-500">Počítám odhad…</div>

  const conditionTargets = value.params?.conditionProfile?.length ? value.params.conditionProfile : [value.params?.condition]
  const exactConditionCount = (value.comparables || []).filter((comparable) => conditionTargets.includes(comparable.condition)).length
  const tone = getDealTone(comparison)
  return (
    <div className={`h-full rounded-lg border p-4 text-sm text-gray-800 ${tone.card}`}>
      <div className="font-semibold text-gray-900">{title}</div>
      <div className="mt-1 text-xs text-gray-500">{description}</div>
      <div className={`mt-3 font-semibold ${tone.price}`}>{formatPrice(value.anchorPrice)}</div>
      <div className={`mt-1 text-sm ${tone.price}`}>{formatPrice(value.anchorPpm2)}/m²</div>
      {comparison?.label && <div className={`mt-3 inline-flex rounded-full px-2 py-1 text-[11px] font-medium ${tone.badge}`}>{comparison.label}</div>}
      {publicView ? (
        <ValuationEvidenceSummary value={value} method={method} conditionTargets={conditionTargets} />
      ) : (
        <div className="mt-3 space-y-1 text-xs text-gray-600">
          <div>Metoda: {getValuationMethodLabel(method, value.methodLabel)}</div>
          <div>Použito: {value.compsUsed ?? 0} z {value.availableComparables ?? 0} vhodných nabídek</div>
          <div>{conditionTargets.length > 1 ? 'Shodný profil' : 'Shodný stav'}: {exactConditionCount} z {value.compsUsed ?? 0}</div>
          <div>Důvěra: {Number.isFinite(value.confidence) ? `${Math.round(value.confidence * 100)} %` : '—'}</div>
          <div>Vyhledávací poloměr: {Math.round(value.usedSearchRadiusMeters ?? value.searchRadiusMeters ?? 0)} m</div>
        </div>
      )}
    </div>
  )
}

const FeasibilityCard = ({ property, renovatedValue, preview, loading, error, onOpen }) => {
  const targetPricePerM2 = renovatedValue?.anchorPpm2
  const canPrefillTarget = Number.isFinite(Number(targetPricePerM2))
  const isActive = property?.feasibility?.active === true
  const profitSharePercent = property?.feasibility?.assumptions?.profitSharePercent ?? 100

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md">
      <div className="flex items-start justify-between gap-3 p-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Záměr</h3>
          <p className="mt-1 text-sm text-slate-600">Rekonstrukce a prodej bytu {isActive ? '· aktivní' : ''}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Náhled</span>
      </div>
      <div className="text-sm">
        {isActive && loading ? (
          <div className="border-y border-slate-100 px-4 py-4 text-slate-500">Počítám aktuální výsledek…</div>
        ) : preview ? (
          <FeasibilityCalculationResult
            preview={preview}
            profitSharePercent={profitSharePercent}
            embedded
            showVatAndWarnings={false}
          />
        ) : (
          <div className="border-y border-slate-100 px-4 py-4">
            <div className="text-slate-500">Cílová prodejní cena / m²</div>
            <div className="mt-1 font-semibold text-slate-900">{canPrefillTarget ? `${Math.round(targetPricePerM2).toLocaleString('cs-CZ')} Kč/m²` : 'Doplníte v kalkulaci'}</div>
            {canPrefillTarget && <div className="mt-1 text-xs text-slate-500">Předvyplněno z odhadu po rekonstrukci.</div>}
          </div>
        )}
      </div>
      <div className="p-4">
        <button type="button" onClick={onOpen} className="w-full rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800">
          {isActive ? 'Otevřít záměr' : 'Spočítat flip'}
        </button>
        {!property?.price && <p className="mt-2 text-xs text-amber-700">Kupní cena není u nabídky uvedena; před výpočtem ji doplňte.</p>}
        {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      </div>
    </div>
  )
}

const ComparableAnalysis = ({
  title,
  description,
  propertyValue,
  method,
  subjectM2,
  onOpenProperty,
  onHoverProperty,
  onCreateValuation,
  readOnly = false,
}) => {
  if (!propertyValue) return <div className="rounded-lg bg-gray-100 p-4 text-sm text-gray-500">Načítám srovnání…</div>

  return (
    <div className="space-y-4 pb-5">
      <div className="rounded-lg bg-gray-100 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-600">{description}</p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onCreateValuation}
              className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
            >
              Vytvořit odhad
            </button>
          )}
        </div>
        {readOnly ? (
          <ValuationEvidenceSummary
            value={propertyValue}
            method={method}
            conditionTargets={propertyValue.params?.conditionProfile?.length ? propertyValue.params.conditionProfile : [propertyValue.params?.condition]}
          />
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-600 sm:grid-cols-4">
            <div><span className="block text-gray-500">Metoda</span>{getValuationMethodLabel(method, propertyValue.methodLabel)}</div>
            <div><span className="block text-gray-500">Použito</span>{propertyValue.compsUsed ?? 0} srovnání</div>
            <div><span className="block text-gray-500">Důvěra</span>{Number.isFinite(propertyValue.confidence) ? `${Math.round(propertyValue.confidence * 100)} %` : '—'}</div>
            <div><span className="block text-gray-500">Poloměr</span>{Math.round(propertyValue.usedSearchRadiusMeters ?? propertyValue.searchRadiusMeters ?? 0)} m</div>
          </div>
        )}
      </div>
      <ComparableTable
        properties={propertyValue.comparables || []}
        subjectM2={subjectM2}
        calculationMethod={method}
        onOpenProperty={onOpenProperty}
        onHoverProperty={onHoverProperty}
        showDetailLinks={!readOnly}
        lockedComparablesCount={readOnly ? propertyValue.lockedComparablesCount || 0 : 0}
      />
    </div>
  )
}

const PropertyMap = ({ property, propertyValue, highlightedPropertyId, onComparableClick }) => {
  const valuationData = propertyValue
    ? {
        address: property.location,
        coordinatesLat: propertyValue.params?.lat,
        coordinatesLng: propertyValue.params?.lon,
        layout: propertyValue.params?.layout,
        m2: propertyValue.params?.m2,
        condition: propertyValue.params?.condition,
        comparables: propertyValue.comparables,
        usedSearchRadiusMeters: propertyValue.usedSearchRadiusMeters,
        searchRadiusMeters: propertyValue.searchRadiusMeters,
      }
    : null

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 rounded bg-gray-100 p-4">
        <h3 className="text-base font-semibold">Poloha a použitá srovnání</h3>
        <p className="mt-1 text-xs text-gray-600">
          Červený bod je oceňovaná nemovitost, modré body jsou použitá srovnání. Číslo v bodu označuje více nabídek na stejné adrese; klepnutím je rozbalíte.
        </p>
        {propertyValue && <p className="mt-2 text-xs text-gray-600">Použitý poloměr: {Math.round(propertyValue.usedSearchRadiusMeters ?? propertyValue.searchRadiusMeters ?? 0)} m</p>}
      </div>
      <div className="flex-1 rounded-lg border border-gray-300">
        <ListingMap
          valuation={valuationData}
          showComparables
          highlightedPropertyId={highlightedPropertyId}
          onComparableClick={onComparableClick}
        />
      </div>
    </div>
  )
}

const Detail = ({ viewMode = 'private' }) => {
  const { propertyId } = useParams()
  const navigate = useNavigate()
  const demoMode = viewMode === 'demo'
  const [activeTab, setActiveTab] = useState('description')
  const [property, setProperty] = useState(null)
  const [comment, setComment] = useState('')
  const [prompt, setPrompt] = useState('')
  const [showSubmitAnalysis, setShowSubmitAnalysis] = useState(false)
  const [analysis, setAnalysis] = useState('')
  const [floorResponse, setFloorResponse] = useState(null)
  const [renovatedResponse, setRenovatedResponse] = useState(null)
  const [valuationCache, setValuationCache] = useState(null)
  const [valuationCacheError, setValuationCacheError] = useState('')
  const [isValuationCacheRefreshing, setIsValuationCacheRefreshing] = useState(false)
  const [mapScenario, setMapScenario] = useState('renovated')
  const [highlightedComparableId, setHighlightedComparableId] = useState(null)
  const [selectedComparable, setSelectedComparable] = useState(null)
  const [isFeasibilityOpen, setIsFeasibilityOpen] = useState(false)
  const [feasibilityPreview, setFeasibilityPreview] = useState(null)
  const [isFeasibilityLoading, setIsFeasibilityLoading] = useState(false)
  const [feasibilityPreviewError, setFeasibilityPreviewError] = useState('')
  const [isPropertyEditOpen, setIsPropertyEditOpen] = useState(false)
  const [savingPropertyEdit, setSavingPropertyEdit] = useState(false)
  const [valueRefreshKey, setValueRefreshKey] = useState(0)
  const [loadError, setLoadError] = useState('')
  const skipNextFeasibilityLoad = useRef(false)

  const applyValuationCache = useCallback((cache) => {
    setValuationCache(cache)
    if (cache?.scenarios?.floor?.result) setFloorResponse(cache.scenarios.floor.result)
    if (cache?.scenarios?.renovated?.result) setRenovatedResponse(cache.scenarios.renovated.result)
    if (cache?.status === 'fresh') setIsValuationCacheRefreshing(false)
  }, [])

  useEffect(() => {
    let cancelled = false

    if (demoMode) {
      setLoadError('')
      publicPropertyDemoService
        .fetch(propertyId)
        .then(({ property: publicProperty, valuationCache: publicCache }) => {
          if (cancelled) return
          setProperty(publicProperty)
          applyValuationCache(publicCache)
        })
        .catch((error) => {
          if (!cancelled) setLoadError(error.message || 'Ukázkovou analýzu se nepodařilo načíst')
        })

      return () => {
        cancelled = true
      }
    }

    const fetchProperty = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/property/${propertyId}`)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (!cancelled) {
          setProperty(data)
          setPrompt(data.promptDetails)
        }
      } catch (error) {
        console.error('Error fetching property:', error)
        if (!cancelled) setLoadError('Nemovitost se nepodařilo načíst.')
      }
    }

    const fetchCachedValues = async () => {
      try {
        const cache = await propertyValuationCacheService.fetchDetail(propertyId)
        if (!cancelled) {
          applyValuationCache(cache)
          setValuationCacheError('')
        }
      } catch (error) {
        console.error('Error fetching cached valuations:', error)
        setValuationCacheError('Odhady se nepodařilo načíst.')
      }
    }

    fetchProperty()
    fetchCachedValues()
    return () => {
      cancelled = true
    }
  }, [applyValuationCache, demoMode, propertyId, valueRefreshKey])

  const valuationCacheNeedsPolling = Boolean(
    !demoMode && valuationCache && (valuationCache.refreshNeeded || valuationCache.refreshing || ['pending', 'calculating'].includes(valuationCache.status))
  )

  useEffect(() => {
    if (!valuationCacheNeedsPolling) return undefined
    let cancelled = false
    let requestRunning = false
    const poll = async () => {
      if (requestRunning) return
      requestRunning = true
      try {
        const cache = await propertyValuationCacheService.fetchDetail(propertyId)
        if (!cancelled) {
          applyValuationCache(cache)
          setValuationCacheError('')
        }
      } catch (error) {
        if (!cancelled) setValuationCacheError('Aktualizaci odhadů se nepodařilo načíst.')
      } finally {
        requestRunning = false
      }
    }
    const intervalId = window.setInterval(poll, 3000)
    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [applyValuationCache, propertyId, valuationCacheNeedsPolling])

  const floorValue = floorResponse?.calculations?.[VALUATION_METHODS.SIMILARITY_MULTIPLICATIVE] || floorResponse
  const renovatedValue = renovatedResponse?.calculations?.[VALUATION_METHODS.SIMILARITY_ARITHMETIC] || renovatedResponse
  const floorDealComparison = getFloorDealComparison(property?.price, floorValue?.anchorPrice)
  const renovatedDealComparison = getRenovatedDealComparison(property?.price, renovatedValue?.anchorPrice)
  const valuationCalculatedAt = [
    valuationCache?.scenarios?.floor?.calculatedAt,
    valuationCache?.scenarios?.renovated?.calculatedAt,
  ]
    .filter(Boolean)
    .sort()
    .at(-1)
  const valuationScenarioError = valuationCache?.scenarios?.floor?.error || valuationCache?.scenarios?.renovated?.error

  useEffect(() => {
    if (demoMode) return undefined
    if (!property?.feasibility?.active) {
      setFeasibilityPreview(null)
      setFeasibilityPreviewError('')
      return undefined
    }
    const hasTargetOverride = Number.isFinite(Number(property.feasibility?.overrides?.targetPricePerM2))
    if (!hasTargetOverride && !Number.isFinite(Number(renovatedValue?.anchorPpm2))) return undefined
    if (skipNextFeasibilityLoad.current) {
      skipNextFeasibilityLoad.current = false
      return undefined
    }

    let cancelled = false
    setIsFeasibilityLoading(true)
    setFeasibilityPreviewError('')
    try {
      const form = getFeasibilityForm(property, renovatedValue)
      const { previewPayload } = prepareFeasibilityCalculation(property, renovatedValue, form)
      feasibilityService.previewFlatFlip(previewPayload)
        .then((preview) => {
          if (!cancelled) setFeasibilityPreview(preview)
        })
        .catch((error) => {
          if (!cancelled) setFeasibilityPreviewError(error.message || 'Aktuální kalkulaci Záměru se nepodařilo načíst.')
        })
        .finally(() => {
          if (!cancelled) setIsFeasibilityLoading(false)
        })
    } catch (error) {
      setFeasibilityPreviewError(error.message || 'Vstupy Záměru nejsou kompletní.')
      setIsFeasibilityLoading(false)
    }
    return () => { cancelled = true }
  }, [demoMode, property, renovatedValue])

  // Create header content for the Layout
  const headerContent = (
    <div className="flex items-center gap-2 sm:gap-4">
      <button onClick={() => navigate('/')} className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
      </button>
      <h1 className="text-base sm:text-2xl font-semibold text-gray-900">Detail nemovitosti</h1>
    </div>
  )

  const renderShell = (content) =>
    demoMode ? (
      <PublicValuationLayout
        viewMode="demo"
        title={property?.title ? `${property.title} · ukázková analýza` : 'Ukázková analýza nemovitosti'}
        description={property ? `${property.title} · ${property.location} · ${property.m2} m²` : 'Ukázková analýza nemovitosti'}
        image={getGalleryImageUrl(property?.gallery)}
      >
        {content}
      </PublicValuationLayout>
    ) : (
      <Layout headerContent={headerContent}>{content}</Layout>
    )

  if (loadError) {
    return renderShell(
      <div className="flex min-h-64 items-center justify-center px-4">
        <div className="text-center text-red-700">{loadError}</div>
      </div>
    )
  }

  if (!property) return renderShell(<div className="flex min-h-64 items-center justify-center">Načítání…</div>)

  const propertyTitle = property?.title ? `${demoMode ? 'Ukázková analýza' : 'Nemovitost'}: ${property.title}` : 'Detail nemovitosti'
  const propertyDescription = `${demoMode ? 'Ukázková cenová analýza' : 'Detail nemovitosti'} ${property.location || ''} ${
    property.m2 ? `${property.m2} m²` : ''
  } ${property.price ? `${property.price.toLocaleString()} Kč` : ''}${demoMode ? '.' : ' v administraci Realitní Jestřáb.'}`
  const propertyImage = getGalleryImageUrl(property.gallery)
  const mapValue = mapScenario === 'floor' ? floorValue : renovatedValue

  const handleFeasibilityCalculate = async (form) => {
    const { config, previewPayload } = prepareFeasibilityCalculation(property, renovatedValue, form)
    const savedProperty = await feasibilityService.savePropertyFeasibility(property.propertyId, config)
    skipNextFeasibilityLoad.current = true
    setProperty(savedProperty)
    const preview = await feasibilityService.previewFlatFlip(previewPayload)
    setFeasibilityPreview(preview)
    setFeasibilityPreviewError('')
    return preview
  }

  const handlePropertyEdit = async (editedPropertyId, values) => {
    try {
      setSavingPropertyEdit(true)
      const response = await valuationService.updateComparableProperty(editedPropertyId, values)
      setProperty(response.property || property)
      setIsPropertyEditOpen(false)
      setValueRefreshKey((current) => current + 1)
    } catch (error) {
      console.error('Error updating property:', error)
      window.alert(error.message || 'Nemovitost se nepodařilo upravit')
    } finally {
      setSavingPropertyEdit(false)
    }
  }

  const handleRefreshPropertyValuations = async () => {
    try {
      setIsValuationCacheRefreshing(true)
      setValuationCacheError('')
      await propertyValuationCacheService.refresh(propertyId)
      setValuationCache((current) => ({ ...(current || {}), status: 'calculating', refreshing: true }))
    } catch (error) {
      setIsValuationCacheRefreshing(false)
      setValuationCacheError('Odhady se nepodařilo zařadit k aktualizaci.')
    }
  }

  const handleOpenComparable = async (comparable) => {
    setSelectedComparable(comparable)
    if (demoMode) return
    if (!comparable?.propertyId) return
    try {
      const response = await fetch(`${API_BASE_URL}/api/property/${encodeURIComponent(comparable.propertyId)}`)
      if (!response.ok) return
      const sourceProperty = await response.json()
      setSelectedComparable((current) =>
        current?.propertyId === comparable.propertyId
          ? {
              ...sourceProperty,
              ...current,
              gallery: sourceProperty.gallery || current.gallery,
              description: sourceProperty.description || sourceProperty.desc || current.description,
            }
          : current
      )
    } catch (error) {
      console.error('Error loading comparable detail:', error)
    }
  }
  const handleCreateValuation = (scenario, condition, calculationMethod) => {
    const params = scenario?.params
    if (!params?.lat || !params?.lon || !params?.layout || !params?.m2) return

    navigate('/valuations/new', {
      state: {
        prefill: {
          address: property.location || '',
          layout: params.layout,
          m2: params.m2,
          condition,
          floor: params.floor ?? '',
          hasElevator: params.hasElevator === true ? 'true' : params.hasElevator === false ? 'false' : '',
          underRoof: Boolean(params.underRoof),
          coordinates: { lat: params.lat, lng: params.lon },
          calculationMethod,
          sourceProperty: {
            propertyId: String(property.propertyId),
            title: property.title || null,
            location: property.location || null,
            imageUrl: propertyImage || null,
            listingUrl: property.link || null,
          },
        },
      },
    })
  }

  const detailTabs = [
    { id: 'description', label: 'Popis', Icon: FileText },
    { id: 'details', label: 'Parametry', Icon: ListChecks },
    { id: 'map', label: 'Mapa', Icon: MapPinned },
    { id: 'comparables-floor', label: 'Spodní hranice', Icon: BarChart3 },
    { id: 'comparables-renovated', label: 'Po rekonstrukci', Icon: BarChart3 },
    ...(!demoMode ? [{ id: 'comments', label: 'Komentáře', Icon: MessageSquare, mobileOnly: true }] : []),
  ]

  const detailContent = (
    <>
      <Seo
        title={propertyTitle}
        description={propertyDescription.trim()}
        image={propertyImage}
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined}
        noindex={demoMode}
        referrerPolicy={demoMode ? 'strict-origin-when-cross-origin' : undefined}
      />
      {/* Main Content */}
      <div className={`min-h-screen p-3 sm:p-4 ${demoMode ? 'mx-auto max-w-screen-2xl' : 'container mx-0'}`}>
        <Gallery property={property} />

        {!demoMode && <ManualEditHistory property={property} />}

        {/* Main Content Section */}
        <div className="flex flex-col md:flex-row md:h-full gap-6">
          {/* Left Section */}
          <div className={`w-full pb-5 md:h-full ${demoMode ? '' : 'md:w-2/3 md:pr-6'}`}>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-xl sm:text-3xl font-bold">
                {property.title} | {property.condition} | {property.ownership}
              </h1>
              {!demoMode && (
                <button
                  type="button"
                  onClick={() => setIsPropertyEditOpen(true)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Upravit údaje
                </button>
              )}
            </div>
            <div className="mb-5">
              <h2 className="text-lg font-medium text-slate-700 sm:text-2xl">
                {property.price?.toLocaleString()} {property.price ? 'Kč' : 'Cena na dotaz'} {property.m2Price ? `| ${property.m2Price.toLocaleString()} Kč/m²` : ''}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Přidáno: {moment(property.created).format('DD.MM.YYYY')}{' '}
                {property.inactive ? <>| {moment(property.updated).diff(moment(property.created), 'days')} dnů na trhu</> : ''}
              </p>
            </div>

            <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-500">Lokalita</div>
                  <div className="truncate text-sm font-semibold text-slate-950 sm:text-base">{property.location}</div>
                </div>
              </div>
              {property.link && (
                <a
                  href={property.link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  {getLinkHostname(property.link)}
                </a>
              )}
            </div>

            <div className="mb-6">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-normal text-gray-500">
                <div>
                  {valuationCacheNeedsPolling || isValuationCacheRefreshing
                    ? 'Odhady se aktualizují…'
                    : valuationCalculatedAt
                      ? `Odhady aktualizovány ${moment(valuationCalculatedAt).locale('cs').fromNow()}`
                      : 'Odhady zatím nejsou vypočtené'}
                  {(valuationCacheError || valuationScenarioError) && (
                    <span className="ml-2 text-red-700">{valuationCacheError || 'Poslední obnova odhadů selhala.'}</span>
                  )}
                </div>
                {!demoMode && (
                  <button
                    type="button"
                    disabled={valuationCacheNeedsPolling || isValuationCacheRefreshing}
                    onClick={handleRefreshPropertyValuations}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-wait disabled:text-gray-400"
                  >
                    {valuationCacheNeedsPolling || isValuationCacheRefreshing ? 'Aktualizuji…' : 'Aktualizovat odhady'}
                  </button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <ScenarioSummary
                  title="Spodní hranice odhadu"
                  description="Spodní hranice tržního rozpětí: největší váhu mají nabídky před rekonstrukcí a ve špatném stavu."
                  value={floorValue}
                  method={VALUATION_METHODS.SIMILARITY_MULTIPLICATIVE}
                  comparison={floorDealComparison}
                  publicView={demoMode}
                />
                <ScenarioSummary
                  title="Odhad po rekonstrukci"
                  description="Scénář se stavem Po rekonstrukci a relevance jako průměr faktorů."
                  value={renovatedValue}
                  method={VALUATION_METHODS.SIMILARITY_ARITHMETIC}
                  comparison={renovatedDealComparison}
                  publicView={demoMode}
                />
              </div>
            </div>
            <DetailTabs tabs={detailTabs} activeTab={activeTab} onChange={setActiveTab}>
              {activeTab === 'description' && <Description property={property} />}
              {activeTab === 'details' && <PropertyDetails property={property} />}
              {activeTab === 'map' && (
                <div className="pb-2">
                  <div className="mb-4 inline-flex rounded-xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setMapScenario('floor')}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${mapScenario === 'floor' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
                  >
                    Spodní hranice
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapScenario('renovated')}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${mapScenario === 'renovated' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
                  >
                    Po rekonstrukci
                  </button>
                </div>
                <div className="h-[360px] sm:h-[480px] md:h-[800px]">
                  <PropertyMap
                    property={property}
                    propertyValue={mapValue}
                    highlightedPropertyId={highlightedComparableId}
                    onComparableClick={handleOpenComparable}
                  />
                </div>
              </div>
            )}
            {activeTab === 'comparables-floor' && (
              <ComparableAnalysis
                title="Srovnání pro spodní hranici odhadu"
                description="Před rekonstrukcí a Ve špatném stavu mají nejvyšší váhu. U stavu V dobrém stavu rozhoduje, jak levná je nabídka v místním srovnání; lepší stavy jsou silně potlačené."
                propertyValue={floorValue}
                method={VALUATION_METHODS.SIMILARITY_MULTIPLICATIVE}
                subjectM2={property.m2}
                onOpenProperty={handleOpenComparable}
                onHoverProperty={setHighlightedComparableId}
                onCreateValuation={() => handleCreateValuation(floorValue, FLOOR_CONDITION, VALUATION_METHODS.SIMILARITY_MULTIPLICATIVE)}
                readOnly={demoMode}
              />
            )}
            {activeTab === 'comparables-renovated' && (
              <ComparableAnalysis
                title="Srovnání po rekonstrukci"
                description="Stav oceňované nemovitosti je Po rekonstrukci. Relevance je průměrem pěti faktorů."
                propertyValue={renovatedValue}
                method={VALUATION_METHODS.SIMILARITY_ARITHMETIC}
                subjectM2={property.m2}
                onOpenProperty={handleOpenComparable}
                onHoverProperty={setHighlightedComparableId}
                onCreateValuation={() => handleCreateValuation(renovatedValue, 'Po rekonstrukci', VALUATION_METHODS.SIMILARITY_ARITHMETIC)}
                readOnly={demoMode}
              />
            )}
            {/* {activeTab === 'analysis' && (
            <Analysis
              property={property}
              showSubmitAnalysis={showSubmitAnalysis}
              analysis={analysis}
              prompt={prompt}
              setPrompt={setPrompt}
              setAnalysis={setAnalysis}
              setShowSubmitAnalysis={setShowSubmitAnalysis}
            />
          )} */}
            {!demoMode && activeTab === 'comments' && (
              <div className="block md:hidden">
                <Comments property={property} comment={comment} setComment={setComment} handleCommentSubmit={handleCommentSubmit} setProperty={setProperty} />
              </div>
            )}
            </DetailTabs>
          </div>
          {!demoMode && <div className="w-1/3 hidden md:block">
            <Comments property={property} comment={comment} setComment={setComment} handleCommentSubmit={handleCommentSubmit} setProperty={setProperty} />
            <div className="mt-4 p-4 border rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <img src={property.agentPicture} alt={property.agentName} className="w-16 h-16 rounded-full mr-4" />
                <div>
                  <h3 className="text-xl font-bold">{property.agentName}</h3>
                  <p className="text-gray-600">{property.agentPhone}</p>
                  <p className="text-gray-600">{property.agentEmail}</p>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-bold">{property.agencyName}</h4>
                <a href={property.agencyWebsite} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                  {property.agencyWebsite ? new URL(property.agencyWebsite).hostname : ''}
                </a>
              </div>
            </div>
            <FeasibilityCard
              property={property}
              renovatedValue={renovatedValue}
              preview={feasibilityPreview}
              loading={isFeasibilityLoading}
              error={feasibilityPreviewError}
              onOpen={() => setIsFeasibilityOpen(true)}
            />
            <PropertyScrapeDebug property={property} onRequested={() => setValueRefreshKey((current) => current + 1)} />
          </div>}
        </div>
      </div>
      <ComparablePropertyModal
        property={selectedComparable}
        editable={false}
        saving={false}
        onClose={() => setSelectedComparable(null)}
        onSaveOverride={() => {}}
        onSaveProperty={() => {}}
      />
      {!demoMode && <ComparablePropertyModal
        property={isPropertyEditOpen ? {
          ...property,
          sourceValues: property.manualEdits?.originalValues || undefined,
          effectiveValues: {
            m2: property.m2,
            condition: property.condition ?? null,
            floor: property.floor ?? null,
            hasElevator: property.hasElevator ?? property.elevator ?? null,
            underRoof: Boolean(property.underRoof),
          },
        } : null}
        editable
        allowValuationOnly={false}
        showComparisonIgnore
        saving={savingPropertyEdit}
        onClose={() => setIsPropertyEditOpen(false)}
        onSaveOverride={() => {}}
        onSaveProperty={handlePropertyEdit}
      />}
      {!demoMode && <FeasibilityPreviewModal
        isOpen={isFeasibilityOpen}
        property={property}
        renovatedValue={renovatedValue}
        preview={feasibilityPreview}
        onCalculate={handleFeasibilityCalculate}
        onClose={() => setIsFeasibilityOpen(false)}
      />}
    </>
  )

  return renderShell(detailContent)
}

export default Detail
